import { useUpdate } from '@refinedev/core';
import type { PublicationDetailOut } from '@/api/generated/schemas';
import type { ExcludedField } from '../excludedIds';

/**
 * Quick exclude / restore of a single membership (category, collection or tag).
 * `excluded_*_ids` is a full replacement list on PATCH, so we resend the current
 * list ± the toggled id. Pessimistic mode: the response re-splits the id lists,
 * which an optimistic patch cannot reproduce locally.
 */
export const useExclusionToggle = (record: PublicationDetailOut) => {
  const { mutate, mutation } = useUpdate<PublicationDetailOut>();
  const isPending = mutation.isPending;

  const setExcluded = (field: ExcludedField, ids: number[]) =>
    mutate({
      resource: 'user_catalog.publications',
      id: record.id,
      values: { [field]: ids },
      meta: { method: 'patch' },
    });

  const exclude = (field: ExcludedField, id: number) => {
    const current = record[field] ?? [];
    if (current.includes(id)) return;
    setExcluded(field, [...current, id]);
  };

  const restore = (field: ExcludedField, id: number) =>
    setExcluded(
      field,
      (record[field] ?? []).filter((x) => x !== id)
    );

  return { exclude, restore, isPending };
};
